import { taskRailRoutes } from '@modules/Tasks/Resources/js/utils/taskRailRoutes';
import { listObjectTypeForRole } from './contactRoles';

/**
 * Shared route generator for contact type views (customer, supplier, prospect, etc.).
 *
 * Each contact type reuses the same List / Preview / Overview components
 * from the Contacts module, just with a different route-name prefix.
 *
 * @param {string} prefix   - Route-name prefix for child routes (e.g. 'customer', 'contact')
 * @param {string} path     - URL path segment (e.g. 'customers', 'contacts')
 * @param {string} listName - Route name for the list (e.g. 'customers-list', 'contact-list')
 */
export function contactTypeRoutes(prefix, path, listName) {
    return {
        path,
        name: listName,
        component: () => import('@modules/Contacts/Resources/js/pages/List.vue'),
        // What the list asks for as it opens, fetched in one request by the router
        // (utils/listBootstrap.js) — inherited by the records opened inside it.
        meta: { listBootstrap: { list: listObjectTypeForRole(prefix), object: 'contact' } },
        children: [
            {
                path: ':id(\\d+)',
                name: `${prefix}-preview`,
                component: () => import('@modules/Contacts/Resources/js/pages/Preview.vue'),
                redirect: { name: `${prefix}-overview` },
                props: true,
                children: [
                    {
                        path: `/organization/:org/${path}/:id(\\d+)`,
                        name: `${prefix}-overview`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/Overview.vue'),
                        props: true
                    },
                    {
                        path: 'activities',
                        name: `${prefix}-activities`,
                        component: () => import('@/components/base/tabs/TabActivities.vue'),
                        props: true,
                        children: taskRailRoutes(`${prefix}-activities`)
                    },
                    {
                        path: 'tasks',
                        name: `${prefix}-tasks`,
                        component: () => import('@/components/base/tabs/TabTasks.vue'),
                        props: true,
                        children: taskRailRoutes(`${prefix}-tasks`)
                    },
                    {
                        path: 'comments',
                        name: `${prefix}-comments`,
                        component: () => import('@/components/base/tabs/TabComments.vue'),
                        props: true
                    },
                    {
                        path: 'logs',
                        name: `${prefix}-logs`,
                        component: () => import('@/components/base/tabs/TabLogs.vue'),
                        props: true
                    },
                    {
                        path: 'documents/:docPath(.*)*',
                        name: `${prefix}-documents`,
                        component: () => import('@/components/base/tabs/TabDocuments.vue'),
                        props: true
                    },
                    {
                        path: 'shortcuts',
                        name: `${prefix}-shortcuts`,
                        component: () => import('@/components/base/tabs/TabShortcuts.vue'),
                        props: true
                    },
                    {
                        path: 'document-requests',
                        name: `${prefix}-document-requests`,
                        component: () => import('@/components/base/tabs/TabDocumentRequests.vue'),
                        props: true
                    },
                    {
                        path: 'contracts',
                        name: `${prefix}-contracts`,
                        component: () => import('@/components/base/tabs/TabContracts.vue'),
                        props: true
                    },
                    {
                        path: 'ct:tabId(\\d+)',
                        name: `${prefix}-custom-tab`,
                        component: () => import('@/components/base/tabs/TabCustomLayout.vue'),
                        props: true
                    },
                    {
                        path: 'opportunities',
                        name: `${prefix}-opportunities`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/TabOpportunities.vue'),
                        props: true
                    },
                    {
                        path: 'financials',
                        name: `${prefix}-financials`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/TabFinancials.vue'),
                        props: true
                    },
                    {
                        path: 'sales-documents',
                        name: `${prefix}-sales-documents`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/RelatedDocuments.vue'),
                        props: true
                    },
                    {
                        path: 'contacts',
                        name: `${prefix}-contacts`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/TabContacts.vue'),
                        props: true
                    },
                    {
                        path: 'companies',
                        name: `${prefix}-companies`,
                        component: () => import('@modules/Contacts/Resources/js/pages/preview/TabCompanies.vue'),
                        props: true
                    }
                ]
            }
        ]
    };
}
